/**
 * Layout Helper Functions
 * Utilities for manipulating dashboard layouts
 */

import { DashboardLayout, WidgetId, DashboardWidget } from "../types";
import { generateInstanceId } from "./defaultLayout";

/**
 * Adds a new widget to the layout
 */
export function addWidgetToLayout(
  layout: DashboardLayout,
  widgetId: WidgetId,
  position?: { x: number; y: number }
): DashboardLayout {
  // Find the maximum Y position to place new widget at bottom
  const maxY = layout.widgets.length > 0
    ? Math.max(...layout.widgets.map(w => w.y + w.h), 0)
    : 0;

  const newWidget: DashboardWidget = {
    widgetId,
    instanceId: generateInstanceId(widgetId),
    x: position?.x ?? 0,
    y: position?.y ?? maxY,
    w: 6,                    // Default: 50% width
    h: 8,                    // Default: 8 units height
    minW: 3,
    minH: 6,
    maxW: 12,
    visible: true,
  };

  return {
    ...layout,
    widgets: [...layout.widgets, newWidget],
  };
}

/**
 * Removes a widget from the layout by instanceId
 */
export function removeWidgetFromLayout(
  layout: DashboardLayout,
  instanceId: string
): DashboardLayout {
  return {
    ...layout,
    widgets: layout.widgets.filter(w => w.instanceId !== instanceId),
  };
}

/**
 * Duplicates a widget (creates a copy with new instanceId)
 */
export function duplicateWidgetInLayout(
  layout: DashboardLayout,
  instanceId: string
): DashboardLayout {
  const widget = layout.widgets.find(w => w.instanceId === instanceId);
  if (!widget) return layout;

  const duplicatedWidget: DashboardWidget = {
    ...widget,
    instanceId: generateInstanceId(widget.widgetId),
    y: widget.y + widget.h, // Place below original
  };

  return {
    ...layout,
    widgets: [...layout.widgets, duplicatedWidget],
  };
}

/**
 * Updates widget position and size
 */
export function updateWidgetInLayout(
  layout: DashboardLayout,
  instanceId: string,
  updates: Partial<Pick<DashboardWidget, 'x' | 'y' | 'w' | 'h' | 'visible'>>
): DashboardLayout {
  return {
    ...layout,
    widgets: layout.widgets.map(w =>
      w.instanceId === instanceId ? { ...w, ...updates } : w
    ),
  };
}

/**
 * Gets available widgets (not currently on dashboard)
 */
export function getAvailableWidgets(
  layout: DashboardLayout,
  allWidgetIds: WidgetId[]
): WidgetId[] {
  const usedWidgetIds = new Set(layout.widgets.map(w => w.widgetId));
  return allWidgetIds.filter(id => !usedWidgetIds.has(id));
}

/**
 * Gets widget by instanceId
 */
export function getWidgetByInstanceId(
  layout: DashboardLayout,
  instanceId: string
): DashboardWidget | undefined {
  return layout.widgets.find(w => w.instanceId === instanceId);
}

/**
 * Toggles widget visibility
 * If widget doesn't exist, creates it with default position
 */
export function toggleWidgetVisibility(
  layout: DashboardLayout,
  widgetId: WidgetId
): DashboardLayout {
  // Check if widget exists in layout
  const existingWidget = layout.widgets.find(w => w.widgetId === widgetId);
  
  if (existingWidget) {
    // Toggle visibility of existing widget
    return {
      ...layout,
      widgets: layout.widgets.map((widget) =>
        widget.widgetId === widgetId
          ? { ...widget, visible: !widget.visible }
          : widget
      ),
    };
  } else {
    // Widget doesn't exist, create it with default position
    // Find the maximum Y position to place new widget at bottom
    const maxY = layout.widgets.length > 0
      ? Math.max(...layout.widgets.map(w => w.y + w.h), 0)
      : 0;
    
    const currentColumns = layout.cols.lg;
    const widgetWidth = currentColumns === 1 ? 12 : currentColumns === 2 ? 6 : currentColumns === 3 ? 4 : 3;
    const visibleWidgets = layout.widgets.filter(w => w.visible);
    const row = Math.floor(visibleWidgets.length / currentColumns);
    const col = visibleWidgets.length % currentColumns;
    
    const newWidget: DashboardWidget = {
      widgetId,
      instanceId: generateInstanceId(widgetId),
      x: col * widgetWidth,
      y: row * 8,
      w: widgetWidth,
      h: 8,
      minW: 3,
      minH: 6,
      maxW: 12,
      visible: true,
    };
    
    return {
      ...layout,
      widgets: [...layout.widgets, newWidget],
    };
  }
}

/**
 * Checks if layout has unsaved changes
 */
export function hasLayoutChanges(
  originalLayout: DashboardLayout,
  currentLayout: DashboardLayout
): boolean {
  if (originalLayout.widgets.length !== currentLayout.widgets.length) {
    return true;
  }

  // Check if any widget positions/sizes changed
  for (const widget of currentLayout.widgets) {
    const original = originalLayout.widgets.find(
      w => w.instanceId === widget.instanceId
    );
    
    if (!original) return true;
    if (
      original.x !== widget.x ||
      original.y !== widget.y ||
      original.w !== widget.w ||
      original.h !== widget.h ||
      original.visible !== widget.visible
    ) {
      return true;
    }
  }

  return false;
}
